import { useMemo, useState } from "react"
import {
  AlertTriangle,
  Building07,
  ChevronDown,
  DotsHorizontal,
  LayersThree01,
  Plus,
  Tool01,
  Trash01,
  X,
} from "@untitledui/icons"
import { motion, AnimatePresence } from "motion/react"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import {
  useConnectedBodyshop,
  useDealershipsForBodyshop,
  useDeleteDealership,
  useDisconnectBodyshop,
  useGroupDealerships,
} from "@/lib/api/use-teams"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { queryKeys } from "@/lib/api/query-keys"
import { useOrganization } from "@/lib/organization/organization-context"
import { useTeamPermissions } from "@/lib/team/use-team-permissions"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { DesignMenu } from "@/components/ui/design-menu"
import { TeamMembersTableMini } from "./team-members-table-mini"
import { AddDealershipDialog } from "./add-dealership-dialog"
import { AddBodyshopDialog } from "./add-bodyshop-dialog"
import { DeleteDealershipDialog } from "./delete-dealership-dialog"
import { cx } from "@/lib/utils/cx"

// Section A — group's org chart. Top-down hierarchy: group card →
// Dealerships rail → Body shops rail. Cards expand inline (accordion) on
// click to reveal connections + members directly inside the panel.
// Modals stay only for *create* and *destructive* flows.
export function OrgChartPanel() {
  const { activeGroup, availableGroups, availableBodyShops } = useOrganization()
  const groupTeamId = activeGroup ? Number(activeGroup.id) : null
  const groupName = activeGroup?.name ?? null
  const perms = useTeamPermissions(groupTeamId)

  const { data: dealerships = [] } = useGroupDealerships(activeGroup?.id ?? null)
  const deleteDealershipMutation = useDeleteDealership(groupTeamId)
  const bodyshops = useMemo(
    () =>
      availableBodyShops.map((b) => ({
        teamId: Number(b.id),
        name: b.name,
        city: b.city ?? undefined,
        state: b.state ?? undefined,
      })),
    [availableBodyShops],
  )
  const dealershipForDialog = useMemo(
    () =>
      dealerships.map((d) => ({
        teamId: Number(d.id),
        name: d.name,
        city: d.city ?? undefined,
        state: d.state ?? undefined,
      })),
    [dealerships],
  )

  const [addDealershipOpen, setAddDealershipOpen] = useState(false)
  const [addBodyshopOpen, setAddBodyshopOpen] = useState(false)
  const [openDealershipKey, setOpenDealershipKey] = useState<string | null>(null)
  const [openBodyshopKey, setOpenBodyshopKey] = useState<number | null>(null)
  const [dealershipToDelete, setDealershipToDelete] = useState<{
    id: number
    name: string
  } | null>(null)

  if (!activeGroup || availableGroups.length === 0) {
    return null
  }

  const handleDeleteConfirm = async () => {
    if (!dealershipToDelete) return
    const target = dealershipToDelete
    try {
      await deleteDealershipMutation.mutateAsync(target.id)
      toast.success(`${target.name} removed`)
      setDealershipToDelete(null)
      if (openDealershipKey === String(target.id)) setOpenDealershipKey(null)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't remove this dealership"
      toast.error(msg)
    }
  }

  return (
    <section className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm shadow-zinc-900/[0.02] overflow-hidden">
      <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-zinc-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <LayersThree01 className="size-4 text-zinc-400" aria-hidden />
            <h3 className="text-[15px] font-semibold text-zinc-900">
              Your group's structure
            </h3>
          </div>
          <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
            Manage the dealerships and body shops under{" "}
            <span className="font-semibold text-zinc-700">{groupName}</span>.
          </p>
        </div>
      </header>

      <div className="p-5 space-y-4">
        <div className="rounded-2xl border border-indigo-200/70 bg-gradient-to-br from-indigo-50/60 to-white px-4 py-3 flex items-center gap-3">
          <div className="size-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 grid place-items-center shadow-md shadow-indigo-600/25 shrink-0">
            <LayersThree01 className="size-[18px] text-white" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-zinc-900 truncate">
              {groupName}
            </p>
            <p className="text-[11.5px] text-zinc-500 mt-0.5">
              {dealerships.length} dealership{dealerships.length === 1 ? "" : "s"}
              {" · "}
              {bodyshops.length} body shop{bodyshops.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        <Rail
          title="Dealerships"
          icon={Building07}
          tintClass="text-sky-700"
          count={dealerships.length}
        >
          {dealerships.length === 0 ? (
            <AddRow
              tintClass="border-sky-200/70 bg-sky-50/40 hover:bg-sky-50/60 hover:border-sky-300"
              iconTint="text-sky-600"
              icon={Building07}
              label="Add your first dealership"
              canAdd={perms.canManageOrg}
              onAdd={() => setAddDealershipOpen(true)}
            />
          ) : (
            <>
              {dealerships.map((d) => {
                const key = String(d.id)
                return (
                  <DealershipCard
                    key={key}
                    dealershipTeamId={Number(d.id)}
                    name={d.name}
                    city={d.city}
                    state={d.state}
                    expanded={openDealershipKey === key}
                    onToggle={() =>
                      setOpenDealershipKey(openDealershipKey === key ? null : key)
                    }
                    bodyshops={bodyshops}
                    canManage={perms.canManageOrg}
                    onRemove={() =>
                      setDealershipToDelete({ id: Number(d.id), name: d.name })
                    }
                  />
                )
              })}
              {perms.canManageOrg && (
                <AddRow
                  tintClass="border-sky-200/70 bg-sky-50/30 hover:bg-sky-50/60 hover:border-sky-300"
                  iconTint="text-sky-600"
                  icon={Plus}
                  label="Add another dealership"
                  canAdd
                  onAdd={() => setAddDealershipOpen(true)}
                />
              )}
            </>
          )}
        </Rail>

        <Rail
          title="Body shops"
          icon={Tool01}
          tintClass="text-amber-700"
          count={bodyshops.length}
        >
          {bodyshops.length === 0 ? (
            <AddRow
              tintClass="border-amber-200/70 bg-amber-50/40 hover:bg-amber-50/60 hover:border-amber-300"
              iconTint="text-amber-600"
              icon={Tool01}
              label="Add your first body shop"
              canAdd={perms.canManageOrg}
              onAdd={() => setAddBodyshopOpen(true)}
            />
          ) : (
            <>
              {bodyshops.map((b) => (
                <BodyshopCard
                  key={b.teamId}
                  teamId={b.teamId}
                  name={b.name}
                  city={b.city}
                  state={b.state}
                  expanded={openBodyshopKey === b.teamId}
                  onToggle={() =>
                    setOpenBodyshopKey(openBodyshopKey === b.teamId ? null : b.teamId)
                  }
                />
              ))}
              {perms.canManageOrg && (
                <AddRow
                  tintClass="border-amber-200/70 bg-amber-50/30 hover:bg-amber-50/60 hover:border-amber-300"
                  iconTint="text-amber-600"
                  icon={Plus}
                  label="Add another body shop"
                  canAdd
                  onAdd={() => setAddBodyshopOpen(true)}
                />
              )}
            </>
          )}
        </Rail>
      </div>

      <AddDealershipDialog
        open={addDealershipOpen}
        onOpenChange={setAddDealershipOpen}
        groupTeamId={groupTeamId}
        groupName={groupName}
      />
      <AddBodyshopDialog
        open={addBodyshopOpen}
        onOpenChange={setAddBodyshopOpen}
        dealerships={dealershipForDialog}
      />
      <DeleteDealershipDialog
        dealershipName={dealershipToDelete?.name ?? null}
        groupName={groupName}
        loading={deleteDealershipMutation.isPending}
        onClose={() => setDealershipToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </section>
  )
}

function Rail({
  title,
  icon: Icon,
  tintClass,
  count,
  children,
}: {
  title: string
  icon: typeof Tool01
  tintClass: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cx("size-3.5", tintClass)} aria-hidden />
        <h4 className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
          {title}
        </h4>
        {count > 0 && (
          <span className="text-[11px] font-semibold text-zinc-400 tabular-nums">
            {count}
          </span>
        )}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

// "Add another" row — full-width dashed button at the bottom of each rail.
// Mirrors onboarding's row-list "+ Add row" pattern.
function AddRow({
  tintClass,
  iconTint,
  icon: Icon,
  label,
  canAdd,
  onAdd,
}: {
  tintClass: string
  iconTint: string
  icon: typeof Tool01
  label: string
  canAdd: boolean
  onAdd: () => void
}) {
  if (!canAdd) {
    return (
      <div
        className={cx(
          "rounded-2xl border border-dashed px-4 py-3 flex items-center gap-3",
          tintClass.split(" ").filter((c) => !c.startsWith("hover:")).join(" "),
        )}
      >
        <p className="text-[13px] text-zinc-500">{label}</p>
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={onAdd}
      className={cx(
        "w-full rounded-2xl border border-dashed px-4 py-3 flex items-center gap-3 transition-all duration-150 group",
        tintClass,
      )}
    >
      <div className="size-9 rounded-xl bg-white grid place-items-center shrink-0 shadow-sm transition-transform duration-150 group-hover:scale-105">
        <Icon className={cx("size-[18px]", iconTint)} aria-hidden />
      </div>
      <p className="text-[13.5px] font-semibold text-zinc-700 flex-1 text-left">
        {label}
      </p>
      <Plus
        className={cx(
          "size-4 shrink-0 transition-transform duration-150 group-hover:rotate-90",
          iconTint,
        )}
        aria-hidden
      />
    </button>
  )
}

function DealershipCard({
  dealershipTeamId,
  name,
  city,
  state,
  expanded,
  onToggle,
  bodyshops,
  canManage,
  onRemove,
}: {
  dealershipTeamId: number
  name: string
  city: string | null
  state: string | null
  expanded: boolean
  onToggle: () => void
  bodyshops: Array<{ teamId: number; name: string; city?: string; state?: string }>
  canManage: boolean
  onRemove: () => void
}) {
  const cs = [city, state].filter(Boolean).join(", ")
  const { data: connectedBodyshop } = useConnectedBodyshop(String(dealershipTeamId))
  return (
    <div className="rounded-2xl border border-zinc-200/70 bg-white overflow-hidden hover:border-zinc-300 transition-colors duration-150">
      <div className="w-full flex items-center gap-1 hover:bg-zinc-50/50 transition-colors duration-150">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex-1 min-w-0 px-4 py-3 flex items-center gap-3 text-left"
        >
          <div className="size-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 grid place-items-center shrink-0 shadow-md shadow-sky-600/20">
            <Building07 className="size-5 text-white" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-zinc-900 truncate">{name}</p>
            <p className="text-[11.5px] text-zinc-500 truncate">
              {cs || "Dealership"}
              {connectedBodyshop && (
                <>
                  {" · "}
                  <span className="text-amber-700">
                    Routes to {connectedBodyshop.name}
                  </span>
                </>
              )}
            </p>
          </div>
          <ChevronDown
            className={cx(
              "size-4 text-zinc-400 shrink-0 transition-transform duration-200",
              expanded && "rotate-180",
            )}
            aria-hidden
          />
        </button>
        {canManage && (
          <DesignMenu.Root>
            <DesignMenu.Trigger asChild>
              <button
                type="button"
                className="size-8 mr-2 rounded-lg grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-150 shrink-0"
                aria-label={`Actions for ${name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <DotsHorizontal className="size-4" aria-hidden />
              </button>
            </DesignMenu.Trigger>
            <DesignMenu.Content align="end">
              <DesignMenu.Item
                destructive
                onSelect={onRemove}
                icon={<Trash01 aria-hidden />}
              >
                Remove dealership
              </DesignMenu.Item>
            </DesignMenu.Content>
          </DesignMenu.Root>
        )}
      </div>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-zinc-100 px-4 py-4 space-y-4 bg-zinc-50/30">
              <DealershipConnections
                dealershipTeamId={dealershipTeamId}
                allBodyshops={bodyshops}
                canManage={canManage}
              />
              <TeamMembersTableMini teamId={dealershipTeamId} teamName={name} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function DealershipConnections({
  dealershipTeamId,
  allBodyshops,
  canManage,
}: {
  dealershipTeamId: number
  allBodyshops: Array<{ teamId: number; name: string; city?: string; state?: string }>
  canManage: boolean
}) {
  const queryClient = useQueryClient()
  const { data: connectedBodyshop } = useConnectedBodyshop(String(dealershipTeamId))
  const disconnectMutation = useDisconnectBodyshop(dealershipTeamId)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleConnect = async (bodyshopTeamId: number, bodyshopName: string) => {
    setSubmitting(true)
    try {
      await teamsApi.connectBodyshop(dealershipTeamId, {
        bodyshopName,
      })
      // Invalidate BOTH directions so each side's card subtitle refreshes.
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.connectedBodyshop(String(dealershipTeamId)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.connectedDealerships(String(bodyshopTeamId)),
      })
      toast.success(`Connected ${bodyshopName}`)
      setPickerOpen(false)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't connect this body shop"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!connectedBodyshop) return
    const bodyshopId = Number(connectedBodyshop.id)
    const bodyshopName = connectedBodyshop.name
    try {
      await disconnectMutation.mutateAsync(bodyshopId)
      // Both sides need a refresh — same rule as connect.
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.connectedDealerships(String(bodyshopId)),
      })
      toast.success(`Disconnected ${bodyshopName}`)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't disconnect this body shop"
      toast.error(msg)
    }
  }

  const candidateShops = allBodyshops.filter(
    (b) => !connectedBodyshop || Number(connectedBodyshop.id) !== b.teamId,
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
          Connected body shop
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {connectedBodyshop ? (
          <>
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-amber-200 bg-amber-50 text-[12.5px] font-semibold text-amber-900">
              <Tool01 className="size-3" aria-hidden />
              {connectedBodyshop.name}
            </span>
            {canManage && (
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold text-zinc-600 border border-zinc-200 bg-white hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-40 transition-colors duration-150"
              >
                <X className="size-3" aria-hidden />
                {disconnectMutation.isPending ? "Disconnecting…" : "Disconnect"}
              </button>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-amber-200/70 bg-amber-50/30 text-[12px] text-amber-800">
            <AlertTriangle className="size-3" aria-hidden />
            No body shop yet — estimates can't ship until you connect one
          </span>
        )}
        {canManage && !connectedBodyshop && candidateShops.length > 0 && (
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold text-zinc-700 border border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 transition-colors duration-150"
              >
                <Plus className="size-3" aria-hidden />
                Connect a body shop
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={6}
              className="w-[280px] p-1.5 rounded-2xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/15"
            >
              {candidateShops.map((b) => {
                const cs = [b.city, b.state].filter(Boolean).join(", ")
                return (
                  <button
                    key={b.teamId}
                    type="button"
                    disabled={submitting}
                    onClick={() => handleConnect(b.teamId, b.name)}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left hover:bg-zinc-50 disabled:opacity-50 transition-colors duration-150"
                  >
                    <div className="size-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center shrink-0">
                      <Tool01 className="size-3.5 text-white" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-zinc-900 truncate">
                        {b.name}
                      </p>
                      {cs && (
                        <p className="text-[11px] text-zinc-400 truncate">{cs}</p>
                      )}
                    </div>
                  </button>
                )
              })}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}

function BodyshopCard({
  teamId,
  name,
  city,
  state,
  expanded,
  onToggle,
}: {
  teamId: number
  name: string
  city?: string
  state?: string
  expanded: boolean
  onToggle: () => void
}) {
  const cs = [city, state].filter(Boolean).join(", ")
  const { data: servedDealerships = [] } = useDealershipsForBodyshop(String(teamId))
  return (
    <div className="rounded-2xl border border-zinc-200/70 bg-white overflow-hidden hover:border-zinc-300 transition-colors duration-150">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-zinc-50/50 transition-colors duration-150"
      >
        <div className="size-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center shrink-0 shadow-md shadow-amber-600/20">
          <Tool01 className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-zinc-900 truncate">{name}</p>
          <p className="text-[11.5px] text-zinc-500 truncate">
            {cs || "Body shop"}
            {servedDealerships.length > 0 ? (
              <>
                {" · "}Serves {servedDealerships.length} dealership
                {servedDealerships.length === 1 ? "" : "s"}
              </>
            ) : (
              <>
                {" · "}
                <span className="text-amber-700">Not routed yet</span>
              </>
            )}
          </p>
        </div>
        <ChevronDown
          className={cx(
            "size-4 text-zinc-400 shrink-0 transition-transform duration-200",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-zinc-100 px-4 py-4 space-y-4 bg-zinc-50/30">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-2">
                  Serves dealerships
                </p>
                {servedDealerships.length === 0 ? (
                  <p className="text-[12px] text-zinc-500">
                    No dealerships routed here yet. Connect one from a
                    dealership card above.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {servedDealerships.map((d) => (
                      <span
                        key={d.id}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-sky-200 bg-sky-50 text-[12.5px] font-semibold text-sky-900"
                      >
                        <Building07 className="size-3" aria-hidden />
                        {d.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <TeamMembersTableMini teamId={teamId} teamName={name} teamType="Bodyshop" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
